"use client";

import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { FaSpinner } from "react-icons/fa";
import {
    ChevronDown,
    LogOut,
    Settings,
    LayoutDashboard,
    Moon,
    Sun,
    Menu,
    X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import api from "../../utils/Api";

const NavBar = ({ onLogout, user: propUser, isAuthenticated: propIsAuthenticated }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
    const [hoveredPath, setHoveredPath] = useState(null);

    // Theme State
    const [theme, setTheme] = useState(
        localStorage.getItem("theme") ||
        (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light")
    );

    const { t, i18n } = useTranslation("navbar");
    const menuRef = useRef(null);
    const location = useLocation();
    const dispatch = useDispatch();

    // Avatar State
    const [avatarUrl, setAvatarUrl] = useState(null);
    const [avatarLoading, setAvatarLoading] = useState(false);

    const { user: reduxUser, token } = useSelector((state) => state.auth);

    // Use props if provided, otherwise fallback to Redux
    const user = propUser || reduxUser;
    const isAuthenticated = propIsAuthenticated !== undefined ? propIsAuthenticated : (!!token && !!user);

    // --- Avatar Logic ---
    const loadAvatarFromServer = async (profilePicturePath) => {
        if (!profilePicturePath) {
            setAvatarUrl("/pictures/avatar.avif");
            return;
        }
        if (/^https?:\/\/|^\/\//i.test(profilePicturePath)) {
            setAvatarUrl(profilePicturePath);
            return;
        }
        try {
            setAvatarLoading(true);
            const pathParts = profilePicturePath.split('/');
            const folder = pathParts[0];
            const filename = pathParts[1];
            const response = await api.get(`/files/${folder}/${filename}`, { responseType: 'blob' });
            const blob = new Blob([response.data], { type: response.headers['content-type'] || 'image/jpeg' });
            const url = window.URL.createObjectURL(blob);
            if (avatarUrl && avatarUrl.startsWith('blob:')) window.URL.revokeObjectURL(avatarUrl);
            setAvatarUrl(url);
        } catch (error) {
            console.error("Error loading avatar:", error);
            setAvatarUrl("/pictures/avatar.avif");
        } finally {
            setAvatarLoading(false);
        }
    };

    useEffect(() => {
        if (isAuthenticated && user?.profile_picture) {
            loadAvatarFromServer(user.profile_picture);
        } else {
            setAvatarUrl("/pictures/avatar.avif");
        }
        return () => {
            if (avatarUrl && avatarUrl.startsWith('blob:')) window.URL.revokeObjectURL(avatarUrl);
        };
    }, [user?.profile_picture, isAuthenticated]);

    // --- Theme & Lang Logic ---
    useEffect(() => {
        document.documentElement.lang = i18n.language;
    }, [i18n.language]);

    useEffect(() => {
        const root = window.document.documentElement;
        root.classList.remove("light", "dark");
        root.classList.add(theme);
        localStorage.setItem("theme", theme);
    }, [theme]);

    const toggleTheme = () => setTheme((prev) => (prev === "light" ? "dark" : "light"));

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsLanguageMenuOpen(false);
                setIsUserMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleLanguageSelect = (langCode) => {
        i18n.changeLanguage(langCode);
        localStorage.setItem("appLanguage", langCode);
        setIsLanguageMenuOpen(false);
    };

    const handleLogout = async () => {
        try {
            setIsLoggingOut(true);
            setIsUserMenuOpen(false);
            setIsMenuOpen(false);
            await new Promise((resolve) => setTimeout(resolve, 800));
            if (onLogout) await onLogout();
            else dispatch({ type: "auth/logout" });
        } catch (error) {
            console.error("Logout error:", error);
        } finally {
            setIsLoggingOut(false);
        }
    };

    // --- Configuration ---
    const languages = [
        { code: "fr", flag: "https://flagcdn.com/w160/fr.png", label: "FR" },
        { code: "en", flag: "https://flagcdn.com/w160/gb.png", label: "EN" },
        { code: "ar", flag: "https://flagcdn.com/w160/sa.png", label: "AR" },
    ];

    const navItems = [
        { title: "home", path: "/" },
        { title: "services", path: "/services" },
        { title: "about", path: "/about" },
        { title: "contact", path: "/contact" },
    ];

    const userMenuItems = [
        { title: t("dashboard"), path: "/dashboard", icon: <LayoutDashboard className="h-4 w-4" /> },
        // { title: t("jobOffers"), path: "/job-offers", icon: <Briefcase className="h-4 w-4" /> },
        { title: t("settings"), path: "/dashboard/settings", icon: <Settings className="h-4 w-4" /> },
    ];

    const getUserDisplayName = () => user?.company_name || user?.name || "User";

    return (
        <>
            <nav
                className="sticky top-0 z-50 w-full border-b border-gray-100 dark:border-gray-800 bg-white/80 dark:bg-gray-950/80 backdrop-blur-xl transition-all duration-300"
                ref={menuRef}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16 sm:h-20">

                        {/* Logo */}
                        <Link to="/" className="flex-shrink-0 flex items-center gap-2 group">
                            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                <img src="/logo.png" width="60" height="60" alt="Logo" className="" />
                            </motion.div>
                        </Link>

                        {/* Desktop Navigation - Centered & Modern */}
                        <div className="hidden lg:flex items-center justify-center gap-1 bg-gray-100/50 dark:bg-gray-900/50 p-1.5 rounded-full border border-gray-200/50 dark:border-gray-800/50">
                            {navItems.map((item) => {
                                const isActive = location.pathname === item.path;
                                return (
                                    <Link
                                        key={item.path}
                                        to={item.path}
                                        onMouseEnter={() => setHoveredPath(item.path)}
                                        onMouseLeave={() => setHoveredPath(null)}
                                        className={`relative px-4 py-2 text-sm font-medium transition-colors rounded-full duration-200 z-10 ${isActive
                                            ? "text-gray-900 dark:text-white"
                                            : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                                            }`}
                                    >
                                        {isActive && (
                                            <motion.div
                                                layoutId="navbar-indicator"
                                                className="absolute inset-0 bg-white dark:bg-gray-800 shadow-sm rounded-full -z-10"
                                                transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                                            />
                                        )}
                                        {hoveredPath === item.path && !isActive && (
                                            <motion.div
                                                layoutId="navbar-hover"
                                                className="absolute inset-0 bg-gray-200/50 dark:bg-gray-800/50 rounded-full -z-10"
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                exit={{ opacity: 0 }}
                                            />
                                        )}
                                        <span className="relative z-10">{t(item.title)}</span>
                                    </Link>
                                );
                            })}
                        </div>

                        {/* Right Actions */}
                        <div className="flex items-center gap-3">

                            {/* Language Switcher */}
                            <div className="relative hidden md:block">
                                <button
                                    onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
                                    className="flex items-center justify-center w-9 h-9 rounded-full bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors border border-gray-200 dark:border-gray-800"
                                >
                                    <span className="text-xs font-bold">{languages.find(l => l.code === i18n.language)?.label}</span>
                                </button>
                                <AnimatePresence>
                                    {isLanguageMenuOpen && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                            animate={{ opacity: 1, y: 0, scale: 1 }}
                                            exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                            className="absolute right-0 mt-2 w-32 bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-xl shadow-xl overflow-hidden py-1 z-50"
                                        >
                                            {languages.map((lang) => (
                                                <button
                                                    key={lang.code}
                                                    onClick={() => handleLanguageSelect(lang.code)}
                                                    className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                                                >
                                                    <img src={lang.flag} alt={lang.code} className="w-4 h-3 rounded-sm object-cover" />
                                                    <span>{lang.label}</span>
                                                </button>
                                            ))}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>

                            {/* Theme Toggle */}
                            <button
                                onClick={toggleTheme}
                                className="flex items-center justify-center w-9 h-9 rounded-full bg-gray-50 dark:bg-gray-900 text-amber-500 dark:text-yellow-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors border border-gray-200 dark:border-gray-800"
                            >
                                {theme === "light" ? <Sun size={18} /> : <Moon size={18} />}
                            </button>

                            <div className="h-6 w-px bg-gray-200 dark:bg-gray-800 mx-1 hidden sm:block" />

                            {/* User / Auth */}
                            {isAuthenticated && user ? (
                                <div className="relative">
                                    <motion.button
                                        whileTap={{ scale: 0.95 }}
                                        onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                                        className="flex items-center gap-2 pl-1 pr-1 sm:pr-3 py-1 rounded-full border border-transparent hover:border-gray-200 dark:hover:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900 transition-all"
                                    >
                                        <div className="relative h-8 w-8">
                                            <img
                                                src={avatarUrl || "/pictures/avatar.avif"}
                                                alt="Profile"
                                                className="h-full w-full rounded-full object-cover border border-gray-200 dark:border-gray-700"
                                            />
                                            {avatarLoading && (
                                                <div className="absolute inset-0 bg-black/30 rounded-full flex items-center justify-center">
                                                    <FaSpinner className="w-3 h-3 text-white animate-spin" />
                                                </div>
                                            )}
                                        </div>
                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-200 hidden sm:block max-w-[80px] truncate">
                                            {getUserDisplayName().split(' ')[0]}
                                        </span>
                                        <ChevronDown size={14} className={`text-gray-400 transition-transform duration-200 hidden sm:block ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                                    </motion.button>

                                    <AnimatePresence>
                                        {isUserMenuOpen && (
                                            <motion.div
                                                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                                className="absolute right-0 mt-3 w-56 bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-2xl shadow-2xl py-2 z-50 ring-1 ring-black/5"
                                            >
                                                <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800">
                                                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{getUserDisplayName()}</p>
                                                    <p className="text-xs text-gray-500 truncate mt-0.5">{user.email}</p>
                                                </div>
                                                <div className="p-1.5">
                                                    {userMenuItems.map((item) => (
                                                        <Link
                                                            key={item.path}
                                                            to={item.path}
                                                            onClick={() => setIsUserMenuOpen(false)}
                                                            className="flex items-center gap-3 px-3 py-2 text-sm text-gray-600 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                                                        >
                                                            <span className="text-gray-400 dark:text-gray-500">{item.icon}</span>
                                                            {item.title}
                                                        </Link>
                                                    ))}
                                                </div>
                                                <div className="border-t border-gray-100 dark:border-gray-800 p-1.5 mt-1">
                                                    <button
                                                        onClick={handleLogout}
                                                        disabled={isLoggingOut}
                                                        className="w-full flex items-center gap-3 px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                                                    >
                                                        {isLoggingOut ? <FaSpinner className="animate-spin w-4 h-4" /> : <LogOut className="w-4 h-4" />}
                                                        {t("logout")}
                                                    </button>
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <Link
                                        to="/login"
                                        className="hidden sm:inline-flex px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                                    >
                                        {t("login")}
                                    </Link>
                                    <Link
                                        to="/register"
                                        className="inline-flex items-center justify-center px-5 py-2 text-sm font-semibold text-white transition-all bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 rounded-full shadow-lg shadow-yellow-500/20"
                                    >
                                        {t("register")}
                                    </Link>
                                </div>
                            )}

                            {/* Mobile Menu Button */}
                            <button
                                onClick={() => setIsMenuOpen(true)}
                                className="lg:hidden p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                            >
                                <Menu className="w-6 h-6" />
                            </button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Mobile Drawer */}
            <Dialog open={isMenuOpen} onClose={setIsMenuOpen} className="relative z-50 lg:hidden">
                <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-black/20 backdrop-blur-sm transition-opacity duration-300 data-[closed]:opacity-0"
                />
                <div className="fixed inset-0 overflow-hidden">
                    <div className="absolute inset-0 overflow-hidden">
                        <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
                            <DialogPanel
                                transition
                                className="pointer-events-auto w-screen max-w-xs transform transition duration-300 ease-in-out data-[closed]:translate-x-full"
                            >
                                <div className="flex h-full flex-col overflow-y-scroll bg-white dark:bg-gray-950 shadow-2xl py-6 px-6">

                                    {/* Header */}
                                    <div className="flex items-center justify-between mb-8">
                                        <span className="text-lg font-bold text-gray-900 dark:text-white">Menu</span>
                                        <button
                                            onClick={() => setIsMenuOpen(false)}
                                            className="p-2 -mr-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
                                        >
                                            <X size={20} />
                                        </button>
                                    </div>

                                    {/* Nav Links */}
                                    <div className="flex-1 space-y-1">
                                        {navItems.map((item) => (
                                            <Link
                                                key={item.path}
                                                to={item.path}
                                                onClick={() => setIsMenuOpen(false)}
                                                className={`block px-4 py-3 rounded-xl text-base font-medium transition-colors ${location.pathname === item.path
                                                    ? "bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white"
                                                    : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-900"
                                                    }`}
                                            >
                                                {t(item.title)}
                                            </Link>
                                        ))}
                                    </div>

                                    {/* Bottom Actions */}
                                    <div className="pt-6 border-t border-gray-100 dark:border-gray-800 space-y-4">
                                        {/* Mobile Language & Theme */}
                                        <div className="flex items-center justify-between px-2">
                                            <div className="flex gap-2">
                                                {languages.map((lang) => (
                                                    <button
                                                        key={lang.code}
                                                        onClick={() => handleLanguageSelect(lang.code)}
                                                        className={`text-xs font-bold px-2 py-1 rounded-md ${i18n.language === lang.code
                                                            ? "bg-gray-200 dark:bg-gray-700 text-black dark:text-white"
                                                            : "text-gray-500"
                                                            }`}
                                                    >
                                                        {lang.label}
                                                    </button>
                                                ))}
                                            </div>
                                            <button onClick={toggleTheme} className="p-2 text-gray-500 hover:text-amber-500 transition-colors">
                                                {theme === 'light' ? <Sun size={20} /> : <Moon size={20} />}
                                            </button>
                                        </div>

                                        {isAuthenticated ? (
                                            <button
                                                onClick={handleLogout}
                                                className="w-full flex items-center justify-center gap-2 px-4 py-3 text-red-600 bg-red-50 dark:bg-red-900/10 rounded-xl font-medium"
                                            >
                                                <LogOut size={18} />
                                                {t("logout")}
                                            </button>
                                        ) : (
                                            <div className="space-y-3">
                                                <Link to="/login" onClick={() => setIsMenuOpen(false)} className="block w-full text-center py-3 font-medium text-gray-700 dark:text-gray-300">
                                                    {t("login")}
                                                </Link>
                                                <Link to="/register" onClick={() => setIsMenuOpen(false)} className="block w-full text-center py-3 font-bold text-white bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 rounded-xl shadow-lg">
                                                    {t("register")}
                                                </Link>
                                            </div>
                                        )}
                                    </div>

                                </div>
                            </DialogPanel>
                        </div>
                    </div>
                </div>
            </Dialog>

            {/* Logout Overlay */}
            <AnimatePresence>
                {isLoggingOut && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-white/50 dark:bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center"
                    >
                        <div className="bg-white dark:bg-gray-900 p-6 rounded-2xl shadow-xl flex flex-col items-center gap-3">
                            <FaSpinner className="text-3xl text-yellow-500 animate-spin" />
                            <span className="text-sm font-medium text-gray-600 dark:text-gray-300">{t("loggingOut")}...</span>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default NavBar;
